import { format as d3Format } from 'd3-format';
import type { GeoJsonProperties } from 'geojson';
import { isNil } from 'lodash';
import { useQuery } from 'urql';

import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_V3_TOOLTIP,
  type CompositionV2Entity,
  type CompositionV2Filters,
  type CompositionV2FullSnapshotReq,
  type CompositionV3TooltipQuery,
  type CompositionV3TooltipQueryVariables,
  Dimension1,
  PAY_V2_TOOLTIP_QUERY,
  type PayV2Entity,
  type PayV2EntResp,
  POSTING_SOURCE_IDS,
  POSTING_TOOLTIP,
  POSTINGS_V3_TOOLTIP,
  PositionsEntityKind,
  type PostingFilters,
  type PostingsV3Filters,
  PostingsV3Mode,
  type PostingsV3TooltipQuery,
  type PostingsV3TooltipQueryVariables,
} from '@revelio/data-access';
import { formatterWithExchangeRate } from '@revelio/replots';

import { calculateMarketTightnessMeterValue } from '../../next/CustomDash/components/DataProvider/market-conditions/z-score/calculate-meter-value';
import type { DistributionData } from '../../next/CustomDash/components/DataProvider/types';
import { getDistributionDataFromPayV2 } from '../../next/CustomDash/components/DataProvider/utils/convert-compensation-data';
import { DEFAULT_SKILL_TAXONOMY_ID } from '../../next/CustomDash/filters/contexts/defaultFilters';
import {
  getCompositionV3BreakdownOptions,
  roleCountsLevelFromCompositionFilters,
} from '../../next/CustomDash/filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  mapCompositionV2FilterSelectionsToV3,
  mapTooltipFiltersToCompositionV3Corrections,
  mapTooltipFiltersToCompositionV3Filters,
} from '../../next/CustomDash/filters/utils/composition-v3/map-tooltip-filters-to-v3';
import {
  mapPositionsEntitiesToCompositionV3,
  stripEntitiesFromFilters,
} from '../../next/CustomDash/filters/utils/role-skill-ent-kind';
import { useMonthApiFilters } from '../../next/entity-summary/use-month-api-filters';
import { useUnleashFlag } from '../unleash-flags/use-unleash-flag';
import { useCurrencyPreference } from '../user-preferences/hooks/useCurrencyPreference';

export interface TooltipData {
  header: string;
  headcount?: string;
  growthRate?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showSalaryData?: boolean;
}

export interface UseFetchTooltipDataParams {
  dim1: Dimension1;
  tooltipFeature: GeoJsonProperties | null;
  compositionEntities?: CompositionV2Entity[];
  compositionFilters?: CompositionV2Filters;
  postingFilters?: PostingFilters;
  skipComposition?: boolean;
  skipPosting: boolean; // Users who don't have Posting tab access should skip this query which will result in - postings
  skipCompensation?: boolean;
}

const formatInt = d3Format(',.0f');
const signedPct = d3Format('+.1%');

const formatSignedPct = (value: number): string => {
  const formatted = signedPct(value);
  if (formatted === '+0.0%' || formatted === '−0.0%') return '0%';
  return formatted;
};

export const useFetchTooltipData = ({
  dim1,
  tooltipFeature,
  compositionEntities = [],
  compositionFilters,
  postingFilters = {},
  skipComposition = false,
  skipPosting = false,
  skipCompensation = false,
}: UseFetchTooltipDataParams): TooltipData | null => {
  const { code: currencyCode, exchangeRate } = useCurrencyPreference();
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);
  const currencyFormatter = formatterWithExchangeRate(
    currencyCode,
    exchangeRate
  );
  const hasGranularSelection =
    hasGranularGeoSelection(compositionFilters) ||
    hasGranularGeoSelection(postingFilters);
  const geoFilter = getTooltipGeoFilter(tooltipFeature, hasGranularSelection);
  const mapTooltipEntities = buildTooltipEntities(tooltipFeature, []);
  // When the page's primary entity is non-geographic (e.g. a company or role
  // overview), a hovered geography scopes that entity rather than being the
  // subject itself — keep the primary entity and apply the geography as a
  // filter. Geography and td_users maps still treat the hovered geography as
  // the entity.
  const hasNonGeoPrimaryEntity =
    compositionEntities.length > 0 && dim1 !== Dimension1.Geography;
  const useTooltipEntity =
    !hasNonGeoPrimaryEntity &&
    (mapTooltipEntities.length > 0 ||
      (dim1 === Dimension1.Geography && !isNil(geoFilter)));

  const compositionEntitiesEffective = useTooltipEntity
    ? buildTooltipEntities(tooltipFeature, compositionEntities)
    : compositionEntities;

  const compositionWithTooltipGeoFilters = replaceGeographyFilters(
    compositionFilters,
    useTooltipEntity ? {} : geoFilter
  );

  const postingGeoFilter = useTooltipEntity
    ? getTooltipGeoFilter(tooltipFeature, false)
    : geoFilter;

  const postingWithTooltipGeoFilters = replaceGeographyFilters(
    postingFilters,
    postingGeoFilter
  );

  const dateRangeApiFilters = useMonthApiFilters();

  const {
    raw: _raw,
    start_date: _start_date,
    end_date: _end_date,
    ...compositionFiltersForQuery
  } = compositionWithTooltipGeoFilters ?? {};

  const snapshotDateForComposition = dateRangeApiFilters?.date_range_api_filters
    ?.end_date as string;

  const customRoleForTaxonomy = compositionFiltersForQuery?.custom_role;
  const customSkillForTaxonomy = compositionFiltersForQuery?.custom_skill;
  const role_taxonomy =
    customRoleForTaxonomy?.taxonomyId?.taxonomyId !== undefined &&
    customRoleForTaxonomy.taxonomyId.taxonomyId !== null
      ? customRoleForTaxonomy.taxonomyId.taxonomyId
      : 0;
  const skill_taxonomy =
    customSkillForTaxonomy?.taxonomy_id !== undefined &&
    customSkillForTaxonomy.taxonomy_id !== null
      ? customSkillForTaxonomy.taxonomy_id
      : DEFAULT_SKILL_TAXONOMY_ID;

  const compositionV3FiltersForTooltip =
    mapTooltipFiltersToCompositionV3Filters({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
      snapshotDate: snapshotDateForComposition,
    });

  const compositionV3EntitiesForTooltip =
    mapPositionsEntitiesToCompositionV3(compositionEntitiesEffective) ?? [];

  const compositionV3FiltersStripped = stripEntitiesFromFilters(
    compositionV3FiltersForTooltip,
    compositionV3EntitiesForTooltip
  );

  const compositionV3CorrectionsForTooltip =
    mapTooltipFiltersToCompositionV3Corrections({
      filters: compositionFiltersForQuery as Partial<CompositionV2Filters>,
    });

  const tooltipBreakdownOptions = getCompositionV3BreakdownOptions({
    compositionFilters: compositionV3FiltersStripped,
    geographyMode: 'msa',
  });

  const compositionTooltipVariables: CompositionV3TooltipQueryVariables = {
    entities: compositionV3EntitiesForTooltip,
    filters: compositionV3FiltersStripped,
    corrections: compositionV3CorrectionsForTooltip,
    role_taxonomy,
    skill_taxonomy,
    breakdown_options: {
      geo_breakdown_level: tooltipBreakdownOptions.geo_breakdown_level,
      rics_breakdown_level: tooltipBreakdownOptions.rics_breakdown_level,
      role_breakdown_level: tooltipBreakdownOptions.role_breakdown_level,
      skill_breakdown_level: tooltipBreakdownOptions.skill_breakdown_level,
    },
  };

  const [{ data: compositionTooltipData, fetching: compositionFetching }] =
    useQuery<CompositionV3TooltipQuery, CompositionV3TooltipQueryVariables>({
      query: COMPOSITION_V3_TOOLTIP,
      variables: compositionTooltipVariables,
      pause:
        skipComposition ||
        !tooltipFeature ||
        compositionV3EntitiesForTooltip.length === 0,
    });

  // Weekly date range the posting tooltip reads its latest value from. Prefer the
  // page's posting date filter (a selected range, or the overview snapshot's
  // 2-week window) so the tooltip reflects the selected date, not always the
  // latest week; fall back to the full available range. postings_v3 expects
  // weekly Monday-aligned YYYY-MM-DD dates, which both sources provide.
  const postingStartDate =
    postingWithTooltipGeoFilters?.start_date ??
    dateRangeApiFilters?.date_range_full_api_filters?.start_date;
  const postingEndDate =
    postingWithTooltipGeoFilters?.end_date ??
    dateRangeApiFilters?.date_range_full_api_filters?.end_date;

  const [{ data: postingData, fetching: postingFetching }] = useQuery({
    query: POSTING_TOOLTIP,
    variables: {
      dim1: dim1,
      filters: {
        ...postingWithTooltipGeoFilters,
        start_date: postingStartDate,
        end_date: postingEndDate,
        metric_mode: 'expected_hires',
      },
    },
    pause: skipPosting || !tooltipFeature || isPostingsV3Enabled,
  });

  // The hovered geo / primary entity is the subject, so reuse the composition
  // tooltip's v3 entities and its v2->v3 mapping (which already drops
  // demographics — postings are job ads), swapping in the posting-specific geo.
  // Then strip the entity dims and overlay provider + weekly dates.
  const postingV3FilterSelections = replaceGeographyFilters(
    mapCompositionV2FilterSelectionsToV3(
      compositionFiltersForQuery as Partial<CompositionV2Filters>
    ),
    postingGeoFilter
  );
  const postingV3FiltersStripped = stripEntitiesFromFilters(
    postingV3FilterSelections,
    compositionV3EntitiesForTooltip
  );

  const postingV3Filters: PostingsV3Filters = {
    ...postingV3FiltersStripped,
    provider: [POSTING_SOURCE_IDS.unified],
    start_date: postingStartDate,
    end_date: postingEndDate,
  };

  const postingTooltipVariables: PostingsV3TooltipQueryVariables = {
    req: {
      entities: compositionV3EntitiesForTooltip,
      filters: postingV3Filters,
      // Tooltip uses the `expected_hires` metric mode.
      options: {
        mode: PostingsV3Mode.Hires,
        roles_counts_level: roleCountsLevelFromCompositionFilters(
          postingV3FiltersStripped
        ),
      },
      role_taxonomy,
      skill_taxonomy,
    },
  };

  const [{ data: postingDataV3, fetching: postingV3Fetching }] = useQuery<
    PostingsV3TooltipQuery,
    PostingsV3TooltipQueryVariables
  >({
    query: POSTINGS_V3_TOOLTIP,
    variables: postingTooltipVariables,
    pause: skipPosting || !tooltipFeature || !isPostingsV3Enabled,
  });

  const [{ data: compensationData, fetching: compensationFetching }] = useQuery(
    {
      query: PAY_V2_TOOLTIP_QUERY,
      variables: {
        req: {
          entities: compositionEntitiesEffective as PayV2Entity[],
          filters: compositionWithTooltipGeoFilters,
        },
      },
      pause: skipCompensation || !tooltipFeature,
    }
  );

  const distributionData = getDistributionDataFromPayV2(
    compensationData?.pay_v2?.entities as PayV2EntResp[],
    'total_compensation'
  );

  if (!tooltipFeature) {
    return null;
  }

  const { headcount: headcountFmt, headcountNumber } = (() => {
    if (!compositionTooltipData) return { headcount: '-' };

    const entity = compositionTooltipData.composition_v3?.entities?.[0];
    if (!entity) return { headcount: '-' };
    const headcounts = entity.metrics?.headcount;
    const lastHeadcount = headcounts?.[headcounts.length - 1];
    const headcount = lastHeadcount?.val;
    if (headcount === undefined) return { headcount: '-' };
    return { headcount: formatInt(headcount), headcountNumber: headcount };
  })();

  const growthRateFmt = (() => {
    const points =
      compositionTooltipData?.composition_v3?.entities?.[0]?.metrics
        ?.growth_rate;
    const growth = points?.[points.length - 1]?.val;
    return typeof growth === 'number' ? formatSignedPct(growth) : '-';
  })();

  const {
    postings: postingFmt,
    postingsNumber,
    timeToFill: ttfFmt,
    timeToFillNumber,
  } = (() => {
    const lastValue = (
      points: ReadonlyArray<{ val: number }> | undefined | null
    ) => points?.[points.length - 1]?.val;

    const { activePostings, timeToFill } = isPostingsV3Enabled
      ? (() => {
          const metrics = postingDataV3?.postings_v3?.entities?.[0]?.metrics;
          return {
            activePostings: lastValue(metrics?.active),
            timeToFill: lastValue(metrics?.time_to_fill),
          };
        })()
      : (() => {
          const lastPosting = postingData?.posting?.[0]?.category?.slice(-1)[0];
          return {
            activePostings: lastPosting?.metrics?.active ?? undefined,
            timeToFill: lastPosting?.metrics?.time_to_fill ?? undefined,
          };
        })();

    return {
      postings: activePostings ? formatInt(activePostings) : '-',
      postingsNumber: activePostings,
      timeToFill: timeToFill ? formatInt(timeToFill) : '-',
      timeToFillNumber: timeToFill,
    };
  })();

  const salaryData =
    compositionTooltipData?.composition_v3?.entities?.[0]?.metrics?.salary ??
    [];

  const yearStartWage = salaryData[0]?.val ?? 0;
  const yearEndWage = salaryData[salaryData.length - 1]?.val ?? 0;
  const wageGrowth =
    yearStartWage !== 0 ? (yearEndWage - yearStartWage) / yearStartWage : 0;
  const marketTightness = calculateMarketTightnessMeterValue({
    wageGrowth,
    demandSupply: Number(postingsNumber) / Number(headcountNumber),
    timeToFill: Number(timeToFillNumber),
  });

  return {
    header:
      (tooltipFeature.label || tooltipFeature.location) ?? 'Unknown Geography',
    headcount: headcountFmt,
    growthRate: growthRateFmt,
    postings: postingFmt,
    timeToFill: ttfFmt,
    salaryDist: getPayPercentiles(distributionData, currencyFormatter),
    marketTightness: (marketTightness * 100).toString(),
    isLoading:
      compositionFetching ||
      postingFetching ||
      postingV3Fetching ||
      compensationFetching,
    showSalaryData: !skipCompensation,
  };
};

type GeographyFilterKeys = 'region' | 'country' | 'msa' | 'state' | 'city';

export const replaceGeographyFilters = <T extends Record<string, unknown>>(
  filters: T | undefined,
  geoFilter: Partial<Record<GeographyFilterKeys, unknown>> | null
): T => {
  if (!geoFilter) return (filters ?? {}) as T;

  const {
    region: _region,
    country: _country,
    msa: _msa,
    state: _state,
    city: _city,
    ...rest
  } = (filters ?? {}) as T;

  return {
    ...rest,
    ...geoFilter,
  } as T;
};

const getTooltipGeoFilter = (
  tooltipFeature: GeoJsonProperties | null,
  hasGranularSelection: boolean
):
  | (Pick<
      CompositionV2FullSnapshotReq['filters'],
      'country' | 'msa' | 'city' | 'state'
    > &
      Pick<PostingFilters, 'country' | 'msa' | 'city' | 'state'>)
  | null => {
  if (
    !tooltipFeature ||
    !('id' in tooltipFeature) ||
    !('type' in tooltipFeature)
  ) {
    return null;
  }
  const id = Number(tooltipFeature['id']);
  if (Number.isNaN(id)) return null;

  if (tooltipFeature['type'] === 1) {
    if (hasGranularSelection) return null;
    return { country: [id] };
  }
  if (tooltipFeature['type'] === 2) return { msa: [id] };
  if (tooltipFeature['type'] === 3) return { city: [id] };
  if (tooltipFeature['type'] === 4) return { state: [id] };

  return null;
};

const hasGranularGeoSelection = (
  filters: CompositionV2Filters | PostingFilters | undefined | null
): boolean => {
  if (!filters) return false;

  const hasValues = (
    value: Array<number | string | null | undefined> | null | undefined
  ) => Array.isArray(value) && value.length > 0;

  return (
    hasValues(filters.msa) ||
    hasValues(filters.city) ||
    hasValues(filters.state)
  );
};

const buildTooltipEntities = (
  tooltipFeature: GeoJsonProperties | null,
  fallbackEntities: CompositionV2Entity[]
): CompositionV2Entity[] => {
  if (!tooltipFeature) return fallbackEntities;
  const entId = Number(tooltipFeature['id']);
  if (Number.isNaN(entId)) return fallbackEntities;

  const type = tooltipFeature['type'];
  if (type === 1)
    return [{ EntID: entId, EntType: PositionsEntityKind.Country }];
  if (type === 2) return [{ EntID: entId, EntType: PositionsEntityKind.Msa }];
  if (type === 3) return [{ EntID: entId, EntType: PositionsEntityKind.City }];
  if (type === 4) return [{ EntID: entId, EntType: PositionsEntityKind.State }];

  return fallbackEntities;
};

const getPayPercentiles = (
  distributionData: DistributionData[],
  currencyFormatter: (value: number) => string
) => {
  if (!distributionData || distributionData.length === 0) return [] as string[];
  const first = distributionData[0];
  if (!first || !first.distribution || first.distribution.length === 0) {
    return [] as string[];
  }

  const salaries = first.distribution.map((d) => d.value);
  // Convert CDF ratios (0-1) to percentage scale (0-100) for easier comparison
  const cdf = first.distribution.map((d) => d.cdf * 100);

  const getPercentiles = ({
    salaries = [],
    cdf = [],
    percentiles,
  }: {
    salaries: number[];
    cdf: number[];
    percentiles: number[];
  }) => {
    return percentiles.map((percentile) => {
      let nearest = cdf[0];
      // @ts-expect-error noUncheckedIndexedAccess
      let smallestDiff = Math.abs(cdf[0] - percentile);
      for (let i = 1; i < cdf.length; i++) {
        // @ts-expect-error noUncheckedIndexedAccess
        const difference = Math.abs(cdf[i] - percentile);
        if (difference < smallestDiff) {
          smallestDiff = difference;
          nearest = cdf[i];
        }
      }
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      const cdfIndex = cdf.findIndex((item) => item === nearest);
      return cdfIndex === -1 ? 0 : salaries[cdfIndex] || 0;
    });
  };

  return getPercentiles({
    salaries,
    cdf,
    percentiles: [10, 50, 90],
  }).map((val) => currencyFormatter(val));
};
